import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Quiz de 3 passos que cruza o desejo do visitante (ocasião × cozinha × clima)
 * com a entrega de cada restaurante (cozinha, faixa de preço, ambiente, vila, nota)
 * e recomenda 1 a 3 restaurantes. O parceiro (Fim da Trilha) é sempre recomendado.
 * 100% client-side.
 */

type Answers = { ocasiao?: string; cozinha?: string; local?: string };

// Vilas no mesmo lado da ilha (acesso próximo) — usado para recomendar restaurantes perto de onde a pessoa fica
const MESMO_LADO: Record<string, string[]> = {
  'nova-brasilia': ['farol', 'praia-grande'],
  farol: ['nova-brasilia', 'praia-grande'],
  'praia-grande': ['nova-brasilia', 'farol'],
  encantadas: [],
};

const STEPS = [
  {
    key: 'ocasiao' as const,
    title: 'Qual é a ocasião?',
    options: [
      { v: 'romantico', label: 'Jantar a dois', icon: '💑' },
      { v: 'familia', label: 'Almoço em família', icon: '👨‍👩‍👧' },
      { v: 'amigos', label: 'Rolê com amigos', icon: '🎉' },
      { v: 'rapido', label: 'Comer rápido e barato', icon: '🎒' },
    ],
  },
  {
    key: 'cozinha' as const,
    title: 'Está com vontade de quê?',
    options: [
      { v: 'frutos', label: 'Frutos do mar', icon: '🦐' },
      { v: 'japones', label: 'Japonês / sushi', icon: '🍣' },
      { v: 'brasileira', label: 'Comida caseira / buffet', icon: '🍲' },
      { v: 'cafe', label: 'Café, lanche ou petisco', icon: '☕' },
    ],
  },
  {
    key: 'local' as const,
    title: 'Onde você vai ficar na ilha?',
    options: [
      { v: 'qualquer', label: 'Ainda não sei / tanto faz', icon: '🧭' },
      { v: 'encantadas', label: 'Encantadas', icon: '🏖️' },
      { v: 'nova-brasilia', label: 'Nova Brasília / Praia de Fora', icon: '⛵' },
      { v: 'farol', label: 'Praia do Farol', icon: '🗼' },
    ],
  },
];

function notaNorm(p: any): number {
  if (p.rating?.tripAdvisor) return p.rating.tripAdvisor;
  if (p.rating?.google) return p.rating.google;
  if (p.rating?.booking) return p.rating.booking / 2;
  return 0;
}

function score(p: any, a: Answers): number {
  let s = 0;
  const cuis = (p.cuisine || []).join(' ').toLowerCase();
  const amb = (p.ambiente || '').toLowerCase();
  const preco = p.faixaPreco;
  const nota = notaNorm(p);
  const has = (k: string) => cuis.includes(k);

  // 1) ocasião
  if (a.ocasiao === 'romantico') { if (preco === '$$$') s += 2; else if (preco === '$$') s += 1; if (/romântic|charme|vista|pôr do sol|sofistic|aconcheg|íntim/.test(amb)) s += 2; }
  if (a.ocasiao === 'familia') { if (has('frutos') || has('brasileira') || has('pizza') || has('massa') || has('buffet')) s += 2; if (preco !== '$$$') s += 1; }
  if (a.ocasiao === 'amigos') { if (/música|bar|petisco|ao vivo/.test(amb)) s += 2; if (has('hambúrg') || has('pizza') || has('libanesa')) s += 1; }
  if (a.ocasiao === 'rapido') { if (preco === '$') s += 2; if (has('hambúrg') || has('café') || has('lanch') || has('pizza')) s += 2; }

  // 2) cozinha desejada
  if (a.cozinha === 'frutos') { if (has('frutos') || has('paella') || has('peixe') || has('caiçara') || has('espanhola')) s += 4; }
  if (a.cozinha === 'japones') { if (has('japon') || has('sushi')) s += 5; }
  if (a.cozinha === 'brasileira') { if (has('brasileira') || has('caiçara') || has('buffet') || has('massa') || has('contemporân')) s += 3; }
  if (a.cozinha === 'cafe') { if (has('café') || has('confeit') || has('hambúrg') || /café da manhã|brunch/.test(amb)) s += 3; }

  // 3) localização — recomenda restaurantes perto de onde a pessoa vai ficar na ilha
  if (a.local && a.local !== 'qualquer') {
    if (p.vilarejo === a.local) s += 4;
    else if ((MESMO_LADO[a.local] || []).includes(p.vilarejo)) s += 1.5;
  }
  if (a.local === 'qualquer') s += nota >= 4.5 ? 2 : nota >= 4.2 ? 1 : 0;

  // desempate por reputação + destaque
  s += nota >= 4.6 ? 1.5 : nota >= 4.3 ? 0.8 : 0;
  if (p.featured) s += 0.5;
  return s;
}

function reasons(p: any, a: Answers): string[] {
  const out: string[] = [];
  const cuis = (p.cuisine || []).join(' ').toLowerCase();
  if (cuis.includes('frutos') || cuis.includes('paella')) out.push('Frutos do mar');
  else if (cuis.includes('japon') || cuis.includes('sushi')) out.push('Sushi');
  else if (p.cuisine?.[0]) out.push(p.cuisine[0]);
  if (a.ocasiao === 'rapido' && p.faixaPreco === '$') out.push('Bom e barato');
  if (a.ocasiao === 'romantico' && p.faixaPreco === '$$$') out.push('Para uma ocasião especial');
  if (a.local && a.local !== 'qualquer' && p.vilarejo === a.local) out.push('Pertinho de onde você fica');
  out.push(p.vilarejoName);
  return out.filter(Boolean).slice(0, 3);
}

function notaLabel(p: any): string | null {
  if (p.rating?.tripAdvisor) return `${Number(p.rating.tripAdvisor).toFixed(1)} TripAdvisor`;
  if (p.rating?.google) return `${Number(p.rating.google).toFixed(1)} Google`;
  return null;
}

export function RestauranteQuiz({ restaurantes }: { restaurantes: any[] }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [done, setDone] = useState(false);

  const pick = (key: string, v: string) => {
    const next = { ...answers, [key]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const reset = () => { setAnswers({}); setStep(0); setDone(false); };

  let results: any[] = [];
  if (done) {
    const ranked = restaurantes
      .map(p => ({ p, s: score(p, answers) }))
      .sort((a, b) => b.s - a.s);
    const top = ranked[0]?.s ?? 0;
    let group = ranked.filter(r => r.s >= top - 1.6).slice(0, 3);
    const PARCEIRO = 'fim-da-trilha';
    const parceiro = ranked.find(r => r.p.handle === PARCEIRO);
    // O Fim da Trilha (parceiro) fica em Encantadas — a regra de recomendá-lo sempre
    // só vale quando o visitante vai ficar em Encantadas. Nas outras vilas ele entra só por mérito.
    const aplicaParceiro = answers.local === 'encantadas';
    // Em Encantadas: "jantar a dois" + "frutos do mar" → Fim da Trilha em 1º lugar.
    const forcarTopo = aplicaParceiro && answers.ocasiao === 'romantico' && answers.cozinha === 'frutos';
    if (forcarTopo && parceiro) {
      group = [parceiro, ...group.filter(r => r.p.handle !== PARCEIRO)].slice(0, 3);
    } else if (aplicaParceiro && parceiro && !group.some(r => r.p.handle === PARCEIRO)) {
      // Em Encantadas, garante o parceiro como última opção quando não está no topo.
      group = [...group.slice(0, 2), parceiro];
    }
    results = group.map(r => r.p);
  }

  return (
    <section className="mb-16 bg-teal-deep -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="quiz">
      <div className="max-w-3xl mx-auto text-center">
        <span className="font-body text-sm text-sand-200 font-semibold uppercase tracking-wider">Teste rápido</span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-1 mb-2">Qual restaurante combina com você?</h2>
        <p className="font-body text-sand-100/90 mb-8">Responda 3 perguntas e eu indico onde comer com a sua cara.</p>

        {!done ? (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <div className="flex items-center gap-2 mb-5">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-coral' : 'bg-sand-200'}`} />
              ))}
            </div>
            <p className="font-body text-xs text-warm-gray mb-1">Passo {step + 1} de {STEPS.length}</p>
            <h3 className="font-display text-xl sm:text-2xl text-warm-dark mb-5">{STEPS[step].title}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {STEPS[step].options.map(opt => (
                <button
                  key={opt.v}
                  onClick={() => pick(STEPS[step].key, opt.v)}
                  className="flex items-center gap-3 p-4 rounded-xl border border-sand-200 hover:border-coral hover:bg-coral/5 transition-all text-left"
                >
                  <span className="text-2xl flex-shrink-0">{opt.icon}</span>
                  <span className="font-body font-medium text-warm-dark">{opt.label}</span>
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-5 font-body text-sm text-warm-gray hover:text-warm-dark">
                ← Voltar
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <p className="font-body text-sm text-coral font-semibold uppercase tracking-wider mb-1">
              {results.length > 1 ? 'Suas melhores opções' : 'O restaurante com a sua cara'}
            </p>
            <h3 className="font-display text-2xl text-warm-dark mb-5">Encontrei {results.length === 1 ? 'o restaurante certo' : `${results.length} restaurantes`} pra você</h3>
            <div className="space-y-4">
              {results.map((p, i) => {
                const n = notaLabel(p);
                return (
                  <div key={p.handle} className={`flex gap-4 p-3 rounded-xl border ${i === 0 ? 'border-coral/40 bg-coral/5' : 'border-sand-200'}`}>
                    <Link to={`/restaurante/${p.handle}`} className="block w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={96} height={96} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {i === 0 && <span className="font-body text-[11px] font-bold text-coral uppercase tracking-wide">★ Melhor combinação</span>}
                        {p.type === 'restaurante-parceiro' && <span className="font-body text-[11px] font-bold text-teal-deep uppercase tracking-wide">✓ Parceiro do guia</span>}
                      </div>
                      <h4 className="font-display text-lg text-warm-dark leading-tight">{p.title}</h4>
                      <div className="flex flex-wrap gap-1.5 my-1.5">
                        {reasons(p, answers).map((r, j) => (
                          <span key={j} className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">{r}</span>
                        ))}
                        {n && <span className="font-body text-[11px] bg-teal-light/15 text-teal-deep px-2 py-0.5 rounded-full">⭐ {n}</span>}
                      </div>
                      <Link to={`/restaurante/${p.handle}`} className="font-body text-sm font-semibold text-teal-deep hover:underline">
                        Ver detalhes completos do restaurante →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={reset} className="mt-6 font-body text-sm text-warm-gray hover:text-warm-dark">↻ Refazer o teste</button>
          </div>
        )}
      </div>
    </section>
  );
}
